<template>
  <div class="pwa-employees">
    <!-- 헤더 -->
    <div class="emp-header">
      <div class="header-row">
        <span class="page-title">직원</span>
        <span class="emp-count">{{ employeeSummary.active }}/{{ employeeSummary.total }}명</span>
      </div>
      <div class="search-box">
        <v-icon size="18" color="#9ca3af">mdi-magnify</v-icon>
        <input v-model="searchKeyword" type="text" placeholder="이름, 연락처 검색" />
      </div>
    </div>

    <!-- 필터 칩 -->
    <div class="filter-chips">
      <button :class="{ active: currentFilter === 'all' }" @click="setFilter('all')">전체</button>
      <button :class="{ active: currentFilter === 'active' }" @click="setFilter('active')">재직</button>
      <button :class="{ active: currentFilter === 'yobo' }" @click="setFilter('yobo')">요양보호사</button>
      <button :class="{ active: currentFilter === 'sabok' }" @click="setFilter('sabok')">사회복지사</button>
    </div>

    <!-- 직원 목록 -->
    <div class="emp-list">
      <template v-if="isLoading">
        <div v-for="n in 8" :key="n" class="emp-item skeleton">
          <div class="skeleton-avatar"></div>
          <div class="skeleton-content">
            <div class="skeleton-text"></div>
            <div class="skeleton-text short"></div>
          </div>
        </div>
      </template>

      <div v-else-if="filteredEmployees.length === 0" class="empty-state">
        <v-icon size="48" color="#d1d5db">mdi-account-search</v-icon>
        <span>검색 결과가 없습니다</span>
      </div>

      <template v-else>
        <div
          v-for="employee in filteredEmployees"
          :key="employee.emp_idx"
          class="emp-item"
          @click="openEmployeeDetail(employee)"
        >
          <div class="emp-avatar" :class="getJobColor(employee.emp_job)">
            {{ employee.emp_name?.charAt(0) }}
          </div>
          <div class="emp-info">
            <div class="emp-row">
              <span class="emp-name">{{ employee.emp_name }}</span>
              <span class="emp-status" :class="employee.emp_status">
                {{ getStatusText(employee.emp_status) }}
              </span>
            </div>
            <div class="emp-meta">
              <span class="job">{{ employee.emp_job }}</span>
              <span class="divider">·</span>
              <span class="tenure">{{ getTenure(employee.emp_in_date) }}</span>
            </div>
          </div>
          <div class="emp-actions">
            <button class="action-btn call" @click.stop="callEmployee(employee)" v-if="employee.emp_phone">
              <v-icon size="16">mdi-phone</v-icon>
            </button>
            <v-icon size="18" color="#ccc">mdi-chevron-right</v-icon>
          </div>
        </div>
      </template>
    </div>

    <!-- 직원 상세 바텀시트 -->
    <div class="sheet-overlay" :class="{ open: showDetailSheet }" @click="showDetailSheet = false"></div>
    <div class="sheet" :class="{ open: showDetailSheet }">
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <span class="sheet-title">직원 정보</span>
        <button class="sheet-close" @click="showDetailSheet = false">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>
      <div class="sheet-content" v-if="selectedEmployee">
        <!-- 프로필 -->
        <div class="profile-section">
          <div class="profile-avatar" :class="getJobColor(selectedEmployee.emp_job)">
            {{ selectedEmployee.emp_name?.charAt(0) }}
          </div>
          <div class="profile-info">
            <h2 class="profile-name">{{ selectedEmployee.emp_name }}</h2>
            <span class="profile-job">{{ selectedEmployee.emp_job }}</span>
            <span class="profile-status" :class="selectedEmployee.emp_status">
              {{ getStatusText(selectedEmployee.emp_status) }}
            </span>
          </div>
        </div>

        <!-- 빠른 액션 -->
        <div class="quick-actions" v-if="selectedEmployee.emp_phone">
          <button class="qa-btn" @click="callEmployee(selectedEmployee)">
            <v-icon size="20" color="#1e3a8a">mdi-phone</v-icon>
            <span>전화</span>
          </button>
          <button class="qa-btn" @click="sendSms(selectedEmployee)">
            <v-icon size="20" color="#1e3a8a">mdi-message-text</v-icon>
            <span>문자</span>
          </button>
        </div>

        <!-- 최근 급여 (핵심!) -->
        <div class="wage-section" v-if="employeeWageData">
          <div class="section-title">
            <v-icon size="18" color="#1e3a8a">mdi-cash</v-icon>
            <span>최근 급여 ({{ employeeWageData.yearMonth }})</span>
          </div>
          <div class="wage-card">
            <div class="wage-main">
              <span class="wage-label">실지급액</span>
              <span class="wage-value">{{ formatCurrency(employeeWageData.netPay) }}원</span>
            </div>
            <div class="wage-details">
              <div class="wage-row">
                <span>지급액 합계</span>
                <span>{{ formatCurrency(employeeWageData.totalPay) }}원</span>
              </div>
              <div class="wage-row">
                <span>공제액 합계</span>
                <span class="deduction">-{{ formatCurrency(employeeWageData.totalDeduction) }}원</span>
              </div>
            </div>
            <div class="wage-more" @click="goToPayroll">
              상세 내역 보기 <v-icon size="14">mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>
        <div class="wage-section empty" v-else-if="!isLoadingWage">
          <div class="section-title">
            <v-icon size="18" color="#9ca3af">mdi-cash</v-icon>
            <span>최근 급여</span>
          </div>
          <div class="no-data">급여 정보가 없습니다</div>
        </div>

        <!-- 기본 정보 -->
        <div class="info-section">
          <div class="section-title">
            <v-icon size="18" color="#1e3a8a">mdi-account</v-icon>
            <span>기본 정보</span>
          </div>
          <div class="info-list">
            <div class="info-item" v-if="selectedEmployee.emp_phone">
              <span class="info-label">연락처</span>
              <a :href="`tel:${selectedEmployee.emp_phone}`" class="info-value link">
                {{ selectedEmployee.emp_phone }}
              </a>
            </div>
            <div class="info-item" v-if="selectedEmployee.emp_birth">
              <span class="info-label">생년월일</span>
              <span class="info-value">{{ formatBirth(selectedEmployee.emp_birth) }}</span>
            </div>
            <div class="info-item" v-if="selectedEmployee.emp_email">
              <span class="info-label">이메일</span>
              <span class="info-value">{{ selectedEmployee.emp_email }}</span>
            </div>
            <div class="info-item" v-if="selectedEmployee.emp_addr">
              <span class="info-label">주소</span>
              <span class="info-value">{{ selectedEmployee.emp_addr }}</span>
            </div>
          </div>
        </div>

        <!-- 재직 정보 -->
        <div class="info-section">
          <div class="section-title">
            <v-icon size="18" color="#1e3a8a">mdi-briefcase</v-icon>
            <span>재직 정보</span>
          </div>
          <div class="info-list">
            <div class="info-item">
              <span class="info-label">입사일</span>
              <span class="info-value">{{ selectedEmployee.emp_in_date || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">근속기간</span>
              <span class="info-value">{{ getTenure(selectedEmployee.emp_in_date) }}</span>
            </div>
            <div class="info-item" v-if="selectedEmployee.emp_out_date">
              <span class="info-label">퇴사일</span>
              <span class="info-value warn">{{ selectedEmployee.emp_out_date }}</span>
            </div>
          </div>
        </div>

        <!-- 계정 정보 -->
        <div class="info-section">
          <div class="section-title">
            <v-icon size="18" color="#1e3a8a">mdi-account-key</v-icon>
            <span>앱 계정</span>
          </div>
          <div class="account-status" :class="selectedEmployee.emp_id ? 'active' : 'inactive'">
            <v-icon size="20" :color="selectedEmployee.emp_id ? '#10b981' : '#9ca3af'">
              {{ selectedEmployee.emp_id ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>
            <div class="account-info">
              <span class="account-label">{{ selectedEmployee.emp_id ? '계정 등록됨' : '계정 미등록' }}</span>
              <span class="account-id" v-if="selectedEmployee.emp_id">ID: {{ selectedEmployee.emp_id }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <LodingComponent :is-visible="isLoading" text="직원 정보를 불러오는 중..." />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';

interface Employee {
  emp_idx?: number;
  emp_code: string;
  emp_id?: string;
  m_sisul_code: string;
  emp_name: string;
  emp_birth: string;
  emp_job: string;
  emp_in_date?: string;
  emp_out_date?: string;
  emp_phone?: string;
  emp_email?: string;
  emp_addr?: string;
  emp_status?: 'ACTIVE' | 'INACTIVE' | 'RETIRING';
}

interface WageData {
  yearMonth: string;
  netPay: number;
  totalPay: number;
  totalDeduction: number;
}

const store = useStore();
const router = useRouter();
const user = computed(() => store.state.user);

const isLoading = ref(false);
const isLoadingWage = ref(false);
const employees = ref<Employee[]>([]);
const searchKeyword = ref('');
const currentFilter = ref('all');
const showDetailSheet = ref(false);
const selectedEmployee = ref<Employee | null>(null);
const employeeWageData = ref<WageData | null>(null);

// 요약
const employeeSummary = computed(() => ({
  total: employees.value.length,
  active: employees.value.filter(e => e.emp_status === 'ACTIVE').length
}));

// 필터링
const filteredEmployees = computed(() => {
  let result = employees.value;

  if (searchKeyword.value) {
    const keyword = searchKeyword.value.toLowerCase();
    result = result.filter(emp =>
      emp.emp_name?.toLowerCase().includes(keyword) ||
      emp.emp_phone?.includes(keyword)
    );
  }

  switch (currentFilter.value) {
    case 'active':
      result = result.filter(e => e.emp_status === 'ACTIVE');
      break;
    case 'yobo':
      result = result.filter(e => e.emp_job === '요양보호사');
      break;
    case 'sabok':
      result = result.filter(e => e.emp_job === '사회복지사');
      break;
  }

  return result;
});

// 조회
const fetchEmployees = async () => {
  if (!user.value?.m_sisul_code) return;

  try {
    isLoading.value = true;
    const response = await axios.get(`/api/emp/facility/${user.value.m_sisul_code}`, {
      withCredentials: true
    });

    if (response.data.success) {
      employees.value = response.data.data || [];
    }
  } catch (error) {
    console.error('직원 조회 오류:', error);
    store.commit('showSnackbar', { text: '직원 정보를 불러오지 못했어요.', color: 'error' });
  } finally {
    isLoading.value = false;
  }
};

// 직원 급여 조회
const fetchEmployeeWage = async (employee: Employee) => {
  if (!user.value?.m_sisul_code || !employee.emp_name) {
    employeeWageData.value = null;
    return;
  }

  try {
    isLoadingWage.value = true;
    
    // 지난달 기준으로 조회
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const ym = `${lastMonth.getFullYear()}${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
    
    const response = await axios.get(`/api/calc/payroll/${user.value.m_sisul_code}/${ym}`, {
      withCredentials: true
    });

    if (response.data.success && response.data.data) {
      // 해당 직원 찾기
      const empData = response.data.data.find((item: any) => 
        item.emp_name === employee.emp_name
      );
      
      if (empData) {
        employeeWageData.value = {
          yearMonth: `${lastMonth.getFullYear()}년 ${lastMonth.getMonth() + 1}월`,
          netPay: empData.netPay || empData.실지급액 || 0,
          totalPay: empData.totalPay || empData.지급액합계 || 0,
          totalDeduction: empData.totalDeduction || empData.공제액합계 || 0
        };
      } else {
        employeeWageData.value = null;
      }
    } else {
      employeeWageData.value = null;
    }
  } catch (error) {
    console.error('급여 조회 오류:', error);
    employeeWageData.value = null;
  } finally {
    isLoadingWage.value = false;
  }
};

// 유틸
const setFilter = (filter: string) => {
  currentFilter.value = filter;
};

const getStatusText = (status?: string) => {
  switch (status) {
    case 'ACTIVE': return '재직';
    case 'INACTIVE': return '퇴사';
    case 'RETIRING': return '퇴사예정';
    default: return '재직';
  }
};

const getJobColor = (job: string) => {
  if (job === '요양보호사') return 'yobo';
  if (job === '사회복지사') return 'sabok';
  if (job === '센터장') return 'sisul';
  return 'default';
};

const getTenure = (inDate?: string) => {
  if (!inDate) return '-';
  const start = new Date(inDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
  const now = new Date();
  const years = now.getFullYear() - start.getFullYear();
  const months = now.getMonth() - start.getMonth();
  const totalMonths = years * 12 + months;
  
  if (totalMonths < 1) return '1개월 미만';
  if (totalMonths < 12) return `${totalMonths}개월`;
  const y = Math.floor(totalMonths / 12);
  const m = totalMonths % 12;
  return m > 0 ? `${y}년 ${m}개월` : `${y}년`;
};

const formatBirth = (birth: string) => {
  if (!birth || birth.length !== 8) return birth;
  return `${birth.substring(0, 4)}.${birth.substring(4, 6)}.${birth.substring(6, 8)}`;
};

const formatCurrency = (value: number) => {
  return value.toLocaleString();
};

// 액션
const openEmployeeDetail = async (employee: Employee) => {
  selectedEmployee.value = employee;
  employeeWageData.value = null;
  showDetailSheet.value = true;
  
  // 급여 정보 조회
  await fetchEmployeeWage(employee);
};

const callEmployee = (employee: Employee) => {
  if (employee.emp_phone) {
    window.location.href = `tel:${employee.emp_phone}`;
  }
};

const sendSms = (employee: Employee) => {
  if (employee.emp_phone) {
    window.location.href = `sms:${employee.emp_phone}`;
  }
};

const goToPayroll = () => {
  showDetailSheet.value = false;
  router.push('/pwa/payroll');
};

onMounted(() => {
  fetchEmployees();
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/fonts.scss';

.pwa-employees {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  font-family: $Noto_Sans_KR !important;
  * { font-family: $Noto_Sans_KR !important; }

  // 헤더
  .emp-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: #fff;
    padding: 12px 16px;
    padding-top: calc(12px + env(safe-area-inset-top, 0px));

    .header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      .page-title { font-size: 22px; font-weight: 700; }
      .emp-count { font-size: 14px; color: #888; }
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #f5f5f5;
      border-radius: 8px;
      padding: 8px 12px;

      input {
        flex: 1;
        border: none;
        background: transparent;
        font-size: 15px;
        outline: none;
        &::placeholder { color: #aaa; }
      }
    }
  }

  // 필터
  .filter-chips {
    display: flex;
    gap: 6px;
    padding: 8px 16px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
    overflow-x: auto;
    &::-webkit-scrollbar { display: none; }

    button {
      flex-shrink: 0;
      padding: 6px 14px;
      border: 1px solid #e0e0e0;
      border-radius: 16px;
      background: #fff;
      font-size: 13px;
      color: #666;

      &.active {
        background: #1e3a8a;
        border-color: #1e3a8a;
        color: #fff;
      }
    }
  }

  // 목록
  .emp-list {
    padding: 8px 12px;

    .emp-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: #fff;
      border-radius: 10px;
      margin-bottom: 8px;
      cursor: pointer;

      &:active { background: #fafafa; }

      &.skeleton {
        .skeleton-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        .skeleton-content {
          flex: 1;
          .skeleton-text {
            height: 14px;
            background: #f0f0f0;
            border-radius: 4px;
            margin-bottom: 6px;
            width: 60%;
            &.short { width: 40%; margin-bottom: 0; }
          }
        }
      }

      .emp-avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        flex-shrink: 0;

        &.yobo { background: linear-gradient(135deg, #1e3a8a, #3b82f6); }
        &.sabok { background: linear-gradient(135deg, #059669, #10b981); }
        &.sisul { background: linear-gradient(135deg, #7c3aed, #a78bfa); }
        &.default { background: linear-gradient(135deg, #64748b, #94a3b8); }
      }

      .emp-info {
        flex: 1;
        min-width: 0;

        .emp-row {
          display: flex;
          align-items: center;
          gap: 6px;

          .emp-name { font-size: 15px; font-weight: 600; color: #111; }
          .emp-status {
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: 500;

            &.ACTIVE { background: #d1fae5; color: #047857; }
            &.INACTIVE { background: #fee2e2; color: #dc2626; }
            &.RETIRING { background: #fef3c7; color: #b45309; }
          }
        }

        .emp-meta {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 2px;
          font-size: 12px;
          color: #888;

          .divider { color: #ccc; }
        }
      }

      .emp-actions {
        display: flex;
        align-items: center;
        gap: 8px;

        .action-btn {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;

          &.call {
            background: #d1fae5;
            color: #059669;
          }
        }
      }
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #999;
    font-size: 14px;
    gap: 12px;
  }

  // 바텀시트
  .sheet-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 200;
    opacity: 0;
    visibility: hidden;
    transition: all 0.25s;
    &.open { opacity: 1; visibility: visible; }
  }

  .sheet {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 16px 16px 0 0;
    z-index: 201;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
    max-height: 90vh;
    overflow: hidden;

    &.open { transform: translateY(0); }

    .sheet-handle {
      width: 36px;
      height: 4px;
      background: #ddd;
      border-radius: 2px;
      margin: 8px auto;
    }

    .sheet-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;

      .sheet-title { font-size: 17px; font-weight: 700; }
      .sheet-close {
        padding: 4px;
        background: #f5f5f5;
        border-radius: 50%;
        border: none;
      }
    }

    .sheet-content {
      padding: 16px;
      overflow-y: auto;
      max-height: calc(90vh - 60px);
    }
  }

  // 프로필
  .profile-section {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 16px;

    .profile-avatar {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      font-weight: 600;
      color: #fff;

      &.yobo { background: linear-gradient(135deg, #1e3a8a, #3b82f6); }
      &.sabok { background: linear-gradient(135deg, #059669, #10b981); }
      &.sisul { background: linear-gradient(135deg, #7c3aed, #a78bfa); }
      &.default { background: linear-gradient(135deg, #64748b, #94a3b8); }
    }

    .profile-info {
      .profile-name { font-size: 20px; font-weight: 700; margin: 0; }
      .profile-job { display: block; font-size: 14px; color: #666; margin-top: 2px; }
      .profile-status {
        display: inline-block;
        font-size: 11px;
        padding: 2px 8px;
        border-radius: 4px;
        margin-top: 4px;

        &.ACTIVE { background: #d1fae5; color: #047857; }
        &.INACTIVE { background: #fee2e2; color: #dc2626; }
      }
    }
  }

  // 빠른 액션
  .quick-actions {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;

    .qa-btn {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 12px;
      background: #f0f4ff;
      border: none;
      border-radius: 10px;
      cursor: pointer;

      span { font-size: 12px; color: #1e3a8a; font-weight: 500; }
    }
  }

  // 급여 섹션
  .wage-section {
    margin-bottom: 16px;

    &.empty .no-data {
      background: #f5f5f5;
      padding: 20px;
      border-radius: 10px;
      text-align: center;
      color: #999;
      font-size: 13px;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-bottom: 10px;
    }

    .wage-card {
      background: linear-gradient(135deg, #1e3a8a, #3b82f6);
      border-radius: 12px;
      padding: 16px;
      color: #fff;

      .wage-main {
        margin-bottom: 12px;

        .wage-label { display: block; font-size: 12px; opacity: 0.8; }
        .wage-value { display: block; font-size: 24px; font-weight: 700; }
      }

      .wage-details {
        background: rgba(255,255,255,0.15);
        border-radius: 8px;
        padding: 10px 12px;
        margin-bottom: 10px;

        .wage-row {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          padding: 4px 0;

          .deduction { color: #fca5a5; }
        }
      }

      .wage-more {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-size: 13px;
        opacity: 0.9;
        cursor: pointer;
      }
    }
  }

  // 정보 섹션
  .info-section {
    margin-bottom: 16px;

    .section-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-bottom: 10px;
    }

    .info-list {
      background: #f9f9f9;
      border-radius: 10px;
      padding: 4px 14px;
    }

    .info-item {
      display: flex;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child { border-bottom: none; }

      .info-label { font-size: 13px; color: #888; }
      .info-value {
        font-size: 13px;
        color: #333;
        font-weight: 500;

        &.link { color: #1e3a8a; text-decoration: none; }
        &.warn { color: #dc2626; }
      }
    }
  }

  // 계정 상태
  .account-status {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px;
    background: #f9f9f9;
    border-radius: 10px;

    .account-info {
      .account-label { display: block; font-size: 14px; font-weight: 500; }
      .account-id { display: block; font-size: 12px; color: #888; margin-top: 2px; }
    }

    &.active { background: #d1fae5; }
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
